import { useContext, useEffect, useMemo, useState } from "react";
import { mergeProductLabels, missingProductIds } from "../model/productLabels";
import { ProductLabelsContext } from "../model/productLabelsContext";
import { useAutocompleteProductsQuery } from "./useAutocompleteProductsQuery";

/**
 * Названия выбранных продуктов поля: из общего ProductLabelsProvider, а
 * без него своим запросом только по id, названия которых поле ещё не
 * знает (cache-first). Полученные названия поле копит у себя (ревью WP-C,
 * M9): удаление чипа меняет набор неизвестных id, но уже известные
 * названия остальных чипов не пропадают до ответа.
 */
export function useProductLabels(
  ids: readonly string[],
  picked: ReadonlyMap<string, string>
): ReadonlyMap<string, string> {
  const shared = useContext(ProductLabelsContext);
  const [ownKnown, setOwnKnown] = useState<ReadonlyMap<string, string>>(
    () => new Map()
  );
  const local = useMemo(
    () =>
      mergeProductLabels(
        ownKnown,
        [...picked].map(([value, label]) => ({ value, label }))
      ),
    [ownKnown, picked]
  );
  const missing = missingProductIds(ids, shared ? picked : local);
  const missingKey = missing.join(",");
  useEffect(() => {
    if (shared && missingKey) shared.request(missingKey.split(","));
  }, [shared, missingKey]);
  const { data } = useAutocompleteProductsQuery({
    variables: { value: missing },
    skip: Boolean(shared) || missing.length === 0,
    fetchPolicy: "cache-first",
  });
  // Ответ переносится в ownKnown при рендере, только если пришло новое.
  const merged = mergeProductLabels(
    ownKnown,
    shared ? [] : (data?.autocompleteProducts?.value ?? [])
  );
  if (merged !== ownKnown) setOwnKnown(merged);
  return useMemo(
    () =>
      shared
        ? mergeProductLabels(
            shared.labels,
            [...picked].map(([value, label]) => ({ value, label }))
          )
        : local,
    [shared, picked, local]
  );
}

/** remember общего провайдера или ничего без него. */
export const useRememberProductLabels = () =>
  useContext(ProductLabelsContext)?.remember;
